import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { lazy, Suspense, useEffect, useState } from 'react';
import { Loading } from '../../core/components/Loading';
import { useAuth } from '../../core/hooks/useAuth';
import { getTwitterAuthLink } from '../../core/services/twitter';
import { getUserWalletData } from '../../core/services/wallet';
import { CertificateCard } from './components/CertificateCard';
import { CertificateDetailDialog } from './components/CertificateDetailDialog';
import { MemoDialog } from './components/MemoDialog';

const MintDialog = lazy(() => import('./components/MintDialog').then((m) => ({ default: m.MintDialog })));

import { RaceFilter } from './components/RaceFilter';
import { TweetDialog } from './components/TweetDialog';
import { useCertificates } from './hooks/useCertificates';

const GRADIENT = 'linear-gradient(135deg, #8a2be2, #FFD700)';

function UploadCard({ onClick }) {
  return (
    <Card
      onClick={onClick}
      sx={{
        height: '100%',
        aspectRatio: '3 / 4',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        border: '2px dashed rgba(138, 43, 226, 0.3)',
        bgcolor: 'transparent',
        boxShadow: 'none',
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          border: '2px dashed rgba(138, 43, 226, 0.6)',
          bgcolor: 'rgba(138, 43, 226, 0.05)',
          boxShadow: '0 0 24px rgba(138, 43, 226, 0.1)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          background: GRADIENT,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 1.5,
        }}
      >
        <AddIcon sx={{ color: '#fff', fontSize: 28 }} />
      </Box>
      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
        完走証を追加
      </Typography>
    </Card>
  );
}

export function DashboardPage() {
  const { user } = useAuth();
  const {
    certificates,
    loading,
    raceNames,
    selectedRace,
    setSelectedRace,
    fetchCertificates,
    removeCertificate,
    updateMemorandum,
  } = useCertificates(user?.uid);

  const [mintOpen, setMintOpen] = useState(false);
  const [detailCert, setDetailCert] = useState(null);
  const [memoCert, setMemoCert] = useState(null);
  const [tweetCert, setTweetCert] = useState(null);
  const [twitterCredential, setTwitterCredential] = useState(null);

  useEffect(() => {
    if (user) {
      fetchCertificates();
      getUserWalletData(user.uid).then((data) => {
        if (data) setTwitterCredential(data.twitterCredential);
      });
    }
  }, [user, fetchCertificates]);

  const handleTweetClick = async (cert) => {
    if (!twitterCredential) {
      const { authUrl, oauthTokenSecret } = await getTwitterAuthLink(`${window.location.origin}/auth/twitter/callback`);
      sessionStorage.setItem('oauthTokenSecret', oauthTokenSecret);
      window.location.href = authUrl;
      return;
    }
    setTweetCert(cert);
  };

  if (loading) return <Loading />;

  return (
    <Box>
      <Typography
        component="h1"
        sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}
      >
        マイ完走証
      </Typography>

      {certificates.length === 0 ? (
        <Box sx={{ maxWidth: 240, mx: 'auto', mt: 4 }}>
          <UploadCard onClick={() => setMintOpen(true)} />
        </Box>
      ) : (
        <>
          <RaceFilter raceNames={raceNames} selectedRace={selectedRace} onSelect={setSelectedRace} />
          <Grid container spacing={2}>
            <Grid size={{ xs: 6, md: 2 }}>
              <UploadCard onClick={() => setMintOpen(true)} />
            </Grid>
            {certificates.map((cert, i) => (
              <Grid key={cert.id} size={{ xs: 6, md: 2 }}>
                <CertificateCard certificate={cert} index={i} onClickCard={setDetailCert} />
              </Grid>
            ))}
          </Grid>
        </>
      )}

      <CertificateDetailDialog
        certificate={detailCert}
        open={!!detailCert}
        onClose={() => setDetailCert(null)}
        onDeleted={removeCertificate}
        onTweet={(cert) => {
          setDetailCert(null);
          handleTweetClick(cert);
        }}
        onMemo={(cert) => {
          setDetailCert(null);
          setMemoCert(cert);
        }}
      />
      <MemoDialog
        certificate={memoCert}
        open={!!memoCert}
        onClose={() => setMemoCert(null)}
        onSaved={updateMemorandum}
      />
      <TweetDialog certificate={tweetCert} open={!!tweetCert} onClose={() => setTweetCert(null)} />
      <Suspense>
        <MintDialog open={mintOpen} onClose={() => setMintOpen(false)} onMinted={fetchCertificates} />
      </Suspense>
    </Box>
  );
}
